import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../Services/toaster";
import "./ApprovalView.css";

// const STATUS_FILTERS = ["All", "Pending", "Approved", "Rejected"];

// ── Remark Modal ──────────────────────────────────────────
function RemarkModal({ action, onConfirm, onCancel }) {
  const [remark, setRemark] = useState("");
  const isApprove = action === "Approved";

  const handleSubmit = () => {
    if (!isApprove && !remark.trim()) {
      toastWarning("Remarks are mandatory for rejection.");
      return;
    }
    onConfirm(remark.trim());
  };

  return (
    <div className="apv-modal-overlay">
      <div className="apv-modal">
        <div className="apv-modal-icon">{isApprove ? "✅" : "❌"}</div>
        <h3 className="apv-modal-title">
          {isApprove ? "Approve Request" : "Reject Request"}
        </h3>
        <p className="apv-modal-sub">
          {isApprove
            ? "Add optional remarks before approving."
            : "Remarks are mandatory before rejecting."}
        </p>
        <textarea
          className="apv-modal-textarea"
          rows={3}
          placeholder={
            isApprove
              ? "Optional remarks…"
              : "Enter reason for rejection (required)…"
          }
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          autoFocus
        />
        <div className="apv-modal-actions">
          <button
            className="apv-modal-btn apv-modal-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`apv-modal-btn ${isApprove ? "apv-modal-btn-approve" : "apv-modal-btn-reject"}`}
            onClick={handleSubmit}
          >
            {isApprove ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approval Card ─────────────────────────────────────────
function ApprovalCard({ row, onApprove, onReject }) {
  // Use API field names from the response body
  const approvalStatus = row.approval_status || "Pending";
  const canAct = row.can_take_action === true;
  const isPending = approvalStatus === "Pending";
  const isApproved = approvalStatus === "Approved";
  const isRejected = approvalStatus === "Rejected";

  const statusColor = {
    Approved: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    Rejected: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    Pending: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  };
  const sc = statusColor[approvalStatus] || statusColor.Pending;

  const barColor = isApproved ? "#A27B5B" : isRejected ? "#A27B5B" : "#A27B5B";

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="apv-card">
      {/* Left accent bar */}
      <div className="apv-card-bar" style={{ background: barColor }} />

      {/* Icon */}
      <div className="apv-card-icon">
        <div className="module-icon">📄</div>
      </div>

      {/* Main info */}
      <div className="apv-card-main">
        <div className="apv-card-title">
          {row.event_title || `Event #${row.event_id}`}
        </div>
        <div className="apv-card-tag">EVENT PUBLISH</div>
        <div className="apv-card-desc">
          Approval required before publishing the event.
        </div>
      </div>

      {/* Divider */}
      <div className="apv-card-divider" />

      {/* Meta */}
      <div className="apv-card-meta">
        <div className="apv-meta-row">
          <span className="apv-meta-key">Module:</span>
          <span className="apv-meta-val">Events</span>
        </div>
        <div className="apv-meta-row">
          <span className="apv-meta-key">Date:</span>
          <span className="apv-meta-val">{fmtDate(row.event_date)}</span>
        </div>
        <div className="apv-meta-row">
          <span className="apv-meta-key">Venue:</span>
          <span className="apv-meta-val">{row.venue || "—"}</span>
        </div>
        <div className="apv-meta-row">
          <span className="apv-meta-key">Level:</span>
          <span className="apv-meta-val">Level {row.approval_level}</span>
        </div>
        <div className="apv-meta-row" style={{ marginTop: 6 }}>
          <span
            className="apv-status-pill"
            style={{
              background: sc.bg,
              color: sc.color,
              border: `1px solid ${sc.border}`,
            }}
          >
            {approvalStatus}
          </span>
        </div>
        {row.remarks && (
          <div className="apv-meta-row" style={{ marginTop: 4 }}>
            <span className="apv-meta-key">Remarks:</span>
            <span className="apv-meta-val" style={{ fontStyle: "italic" }}>
              "{row.remarks}"
            </span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="apv-card-divider" />

      {/* Actions */}
      <div className="apv-card-actions">
        {/* Show Approve/Reject only when Pending AND can_take_action is true */}
        {isPending && canAct ? (
          <>
            <button
              className="apv-btn apv-btn-approve"
              onClick={() => onApprove(row)}
            >
              Approve
            </button>
            <button
              className="apv-btn apv-btn-reject"
              onClick={() => onReject(row)}
            >
              Reject
            </button>
          </>
        ) : isPending && !canAct ? (
          /* Pending but not yet their turn */
          <>
            <div className="apv-actioned" style={{ color: "#b45309" }}>
              ⏳ Awaiting your turn
            </div>
            {/* <button className="apv-btn apv-btn-view">View Details</button> */}
          </>
        ) : (
          /* Already actioned */
          <>
            <div
              className={`apv-actioned ${isApproved ? "apv-actioned-ok" : "apv-actioned-no"}`}
            >
              {isApproved ? "✓ Approved" : "✕ Rejected"}
            </div>
            {row.remarks && <div className="apv-remarks">"{row.remarks}"</div>}
            {/* <button className="apv-btn apv-btn-view">View Details</button> */}
          </>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
//  MAIN
// ═════════════════════════════════════════════════════════
export default function ApprovalView() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter] = useState("All");
  const [remarkModal, setRemarkModal] = useState(null);

  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const API = process.env.REACT_APP_API_URL;

  const hdrs = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    }),
    [token],
  );

  // ── GET /events/my-approvals-events ──────────────────
  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const userId = user?.id || user?.user_id;

      const res = await axios.get(`${API}/events/my-approvals-events`, {
        headers: hdrs(),
        params: {
          user_id: userId,
        },
      });

      setApprovals(res.data?.data || []);
    } catch (err) {
      console.error(err.response?.data);
      toastError("Failed to load approvals.");
    } finally {
      setLoading(false);
    }
  }, [API, hdrs, user]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // ── Open modal for Approve ────────────────────────────
  const handleApprove = (row) => {
    setRemarkModal({ row, action: "Approved" });
  };

  // ── Open modal for Reject ─────────────────────────────
  const handleReject = (row) => {
    setRemarkModal({ row, action: "Rejected" });
  };

  // ── Confirm from modal ────────────────────────────────
  // POST /events/my-approvals-events-action
  const handleConfirm = async (remarks) => {
    const { row, action } = remarkModal;
    setRemarkModal(null);

    try {
      const authToken = useAuthStore.getState().token;
      const userId =
        useAuthStore.getState().user?.id ||
        useAuthStore.getState().user?.user_id ||
        0;

      await axios.post(
        `${API}/events/my-approvals-events-action`,
        {
          id: row.id,
          event_id: row.event_id,
          user_id: userId,
          status: action,
          remarks: remarks,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      toastSuccess(
        action === "Approved"
          ? "Event approved successfully."
          : "Event rejected successfully.",
      );
      fetchApprovals();
    } catch (err) {
      console.error(err.response?.data);
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Action failed. Please try again.";
      toastError(msg);
    }
  };

  // Counts using API field approval_status
  const pending = approvals.filter((r) => r.approval_status === "Pending");
  const approved = approvals.filter((r) => r.approval_status === "Approved");
  const rejected = approvals.filter((r) => r.approval_status === "Rejected");

  const filtered =
    filter === "All"
      ? approvals
      : filter === "Pending"
        ? pending
        : filter === "Approved"
          ? approved
          : rejected;

  return (
    <div className="apv-page">
      {/* Remark modal */}
      {remarkModal && (
        <RemarkModal
          action={remarkModal.action}
          onConfirm={handleConfirm}
          onCancel={() => setRemarkModal(null)}
        />
      )}

      {/* Header */}
      <div className="apv-header">
        <div>
          <h1 className="apv-title">Approvals</h1>
          <p className="apv-sub">
            Review and action pending event approval requests
          </p>
        </div>

        {/* Summary pills */}
        <div className="apv-summary">
          <div className="apv-summary-pill apv-sum-pending">
            <span className="apv-sum-num">{pending.length}</span>
            <span className="apv-sum-lbl">Pending</span>
          </div>
          <div className="apv-summary-pill apv-sum-approved">
            <span className="apv-sum-num">{approved.length}</span>
            <span className="apv-sum-lbl">Approved</span>
          </div>
          <div className="apv-summary-pill apv-sum-rejected">
            <span className="apv-sum-num">{rejected.length}</span>
            <span className="apv-sum-lbl">Rejected</span>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      {/* <div className="apv-tabs">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={`apv-tab ${filter === f ? "apv-tab-on" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
            {f === "Pending" && pending.length > 0 && (
              <span className="apv-tab-dot">{pending.length}</span>
            )}
          </button>
        ))}
      </div> */}

      {/* Cards */}
      {loading ? (
        <div className="apv-empty">Loading approvals…</div>
      ) : filtered.length === 0 ? (
        <div className="apv-empty">
          <div className="apv-empty-icon">✅</div>
          <p>
            No {filter === "All" ? "" : filter.toLowerCase()} approval requests
            found.
          </p>
        </div>
      ) : (
        <div className="apv-list">
          {filtered.map((row, idx) => (
            <ApprovalCard
              key={`${row.id}-${idx}`}
              row={row}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
}
